<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t("pages.driver.historyList.newComment.title") }}</h1>
      </modal-header>
    </template>

    <comment-modal-form
      v-model="rawDriverComment"
      v-model:files="files"
      :loading="anyLoading"
      :errors="errors"
      @save="saveNewComment"
      @openFiles="openFiles"
    />
  </modal>
</template>

<script lang="ts" setup>
import { ref, computed, type Ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import CommentModalForm from '@pages/drivers/driver/CommentModals/CommentModalForm.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { useFileUpload } from '@utils/upload/fileUpload';
import type { Driver, DriverComment, WbFile } from '@/entities/Entities';
import {useStore} from '@/store';

interface Props {
  open: boolean;
  driver: Driver;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const {
  files, open: openFiles, uploadAll, reset: resetFiles, loading: uploading,
} = useFileUpload<WbFile>();

const { t } = useI18n();

const notify = useNotify();
const driver = computed(() => props.driver);

const initialData = (): Omit<Partial<DriverComment>, 'driver'> & { driver: Ref<Driver> } => ({
  driver,
  author: undefined,
  type: undefined,
  text: undefined,
});

const rawDriverComment = ref({ ...initialData() });

const {
  data: driverComment,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    // author: string().nullable().required(t('validation.notEmpty')),
    type: string().nullable().required(t('validation.notEmpty')),
    text: string().nullable().required(t('validation.notEmpty')),
  }),
  rawDriverComment,
);

function resetData() {
  rawDriverComment.value = { ...initialData() } as unknown as typeof rawDriverComment['value'];
  resetFiles();
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const anyLoading = computed(() => loading.value || uploading.value);

const store = useStore();

const currentUser = computed(() => store.state?.auth?.user?.['@id'] ?? null);

const saveNewComment = validatedFn(async () => {
  try {
    const uploadFiles = await uploadAll();

    await exec('/driver_comments', {
      method: 'POST',
      body: {
        driver: driverComment.value.driver['@id'],
        author: currentUser.value,
        type: driverComment.value.type,
        text: driverComment.value.text,
        attachments: uploadFiles?.map((file) => ({ file: file.file['@id'] })),
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.historyList.newComment.saveSuccess'),
    });

    emit('saved');
    resetData();
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.historyList.newComment.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
